import { useMemo, useState } from 'react';
import { FaClock, FaExclamationCircle, FaLayerGroup, FaUserShield } from 'react-icons/fa';
import { usePasStore } from '../pasStore';
import type { Policy } from '../types';
import { formatCurrency, relativeTime } from '../utils';
import { DataTable, type Column } from '../components/DataTable';
import { UnderwritingDecisionPanel } from '../components/UnderwritingDecisionPanel';
import { PageHeader, StatCard } from '../components/primitives';
import { PriorityBadge } from '../components/StatusBadges';

const PRIORITY_RANK: Record<'low' | 'medium' | 'high', number> = { high: 0, medium: 1, low: 2 };

export const UnderwritingQueue: React.FC = () => {
  const policies = usePasStore((s) => s.policies);
  const loading = usePasStore((s) => s.loading);
  const [panelId, setPanelId] = useState<string | null>(null);

  const queue = useMemo(
    () => policies.filter((p) => p.status === 'pending_underwriting'),
    [policies],
  );

  const stats = useMemo(() => {
    const high = queue.filter((p) => p.underwriting.priority === 'high').length;
    const breaches = queue.filter((p) => p.underwriting.slaDueAt && new Date(p.underwriting.slaDueAt).getTime() < Date.now()).length;
    const avgScore = queue.length ? Math.round(queue.reduce((sum, p) => sum + p.risk.score, 0) / queue.length) : 0;
    return { high, breaches, avgScore };
  }, [queue]);

  const columns: Column<Policy>[] = [
    {
      key: 'policyNumber',
      header: 'Submission',
      primary: true,
      sortValue: (p) => p.policyNumber,
      accessor: (p) => (
        <div>
          <span>{p.policyNumber}</span>
          <span className="block text-xs font-normal" style={{ color: 'var(--color-text-secondary)' }}>{p.insured.name}</span>
        </div>
      ),
    },
    { key: 'priority', header: 'Priority', sortValue: (p) => PRIORITY_RANK[p.underwriting.priority], accessor: (p) => <PriorityBadge priority={p.underwriting.priority} /> },
    { key: 'assigned', header: 'Assigned', sortValue: (p) => p.underwriting.assignedTo ?? '', accessor: (p) => <span className="text-sm">{p.underwriting.assignedTo ?? 'Unassigned'}</span> },
    { key: 'premium', header: 'Premium', align: 'right', sortValue: (p) => p.annualPremium, accessor: (p) => <span className="text-sm font-semibold">{formatCurrency(p.annualPremium)}</span> },
    { key: 'submitted', header: 'Submitted', align: 'right', sortValue: (p) => p.underwriting.submittedAt ?? '', accessor: (p) => <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{p.underwriting.submittedAt ? relativeTime(p.underwriting.submittedAt) : '—'}</span> },
    {
      key: 'sla',
      header: 'SLA',
      align: 'right',
      sortValue: (p) => p.underwriting.slaDueAt ?? '',
      accessor: (p) => {
        if (!p.underwriting.slaDueAt) return <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>—</span>;
        const breached = new Date(p.underwriting.slaDueAt).getTime() < Date.now();
        return <span className="text-xs font-semibold" style={{ color: breached ? 'var(--color-error-600)' : 'var(--color-text-secondary)' }}>{relativeTime(p.underwriting.slaDueAt)}</span>;
      },
    },
  ];

  return (
    <div>
      <PageHeader title="Underwriting Queue" subtitle="Review submissions, complete checklists, and record decisions." />

      <div className="mb-6 grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard label="In queue" value={queue.length} icon={<FaLayerGroup />} accent="warning" />
        <StatCard label="High priority" value={stats.high} icon={<FaExclamationCircle />} accent="error" />
        <StatCard label="SLA breaches" value={stats.breaches} icon={<FaClock />} accent="error" />
        <StatCard label="Avg. risk score" value={stats.avgScore || '—'} icon={<FaUserShield />} accent="info" />
      </div>

      <DataTable
        columns={columns}
        data={queue}
        getRowId={(p) => p.id}
        onRowClick={(p) => setPanelId(p.id)}
        loading={loading}
        searchable
        searchPlaceholder="Search submissions…"
        searchText={(p) => `${p.policyNumber} ${p.insured.name} ${p.underwriting.assignedTo ?? ''}`}
        initialSort={{ key: 'priority', direction: 'asc' }}
        emptyIcon={<FaUserShield />}
        emptyTitle="The queue is clear"
        emptyMessage="No submissions are awaiting an underwriting decision."
      />

      <UnderwritingDecisionPanel policyId={panelId} isOpen={!!panelId} onClose={() => setPanelId(null)} />
    </div>
  );
};
